import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import {
  LayoutDashboard, ClipboardList, Users, FileSearch, Briefcase, MessageSquare, Bell, Settings, LogOut,
  Menu as MenuIcon, User as UserIcon, Building2, ChevronDown, ListChecks, BarChart3, Package as PackageIcon,
  CalendarClock, Receipt, FileText, Shield, ScrollText, FileEdit, Target, GitBranch, Lightbulb, UserPlus,
  Search, CreditCard, ShoppingBag, Award, Sliders, Beaker,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const NAV_BY_ROLE = {
  company: [
    { to: "/app", label: "Panoramica", icon: LayoutDashboard },
    { to: "/app/profile", label: "Profilo aziendale", icon: Building2 },
    { to: "/app/checkup", label: "Check-up", icon: ClipboardList },
    { to: "/app/requests", label: "Richieste", icon: FileText },
    { to: "/app/shortlist", label: "Shortlist", icon: Users },
    { to: "/app/browse-managers", label: "Browse Manager", icon: Search },
    { to: "/app/meetings", label: "Incontri", icon: CalendarClock },
    { to: "/app/proposals", label: "Proposte", icon: FileEdit },
    { to: "/app/projects", label: "Progetti", icon: Briefcase },
    { to: "/app/messages", label: "Messaggi", icon: MessageSquare },
    { to: "/app/invoices", label: "Fatture", icon: Receipt },
    { to: "/app/notifications", label: "Notifiche", icon: Bell },
    { to: "/app/subscription", label: "Piano & Abbonamento", icon: CreditCard },
    { to: "/app/settings", label: "Impostazioni", icon: Settings },
  ],
  manager: [
    { to: "/manager", label: "Panoramica", icon: LayoutDashboard },
    { to: "/manager/profile", label: "Profilo", icon: UserIcon },
    { to: "/manager/opportunities", label: "Opportunità", icon: Lightbulb },
    { to: "/manager/open-requests", label: "Marketplace aperto", icon: ShoppingBag },
    { to: "/manager/projects", label: "Progetti", icon: Briefcase },
    { to: "/manager/meetings", label: "Incontri", icon: CalendarClock },
    { to: "/manager/messages", label: "Messaggi", icon: MessageSquare },
    { to: "/manager/referrals", label: "Referral", icon: UserPlus },
    { to: "/manager/notifications", label: "Notifiche", icon: Bell },
    { to: "/manager/subscription", label: "Piano & Abbonamento", icon: CreditCard },
    { to: "/manager/settings", label: "Impostazioni", icon: Settings },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/companies", label: "Aziende", icon: Building2 },
    { to: "/admin/managers", label: "Manager", icon: Users },
    { to: "/admin/applications", label: "Candidature", icon: FileSearch },
    { to: "/admin/requests", label: "Richieste", icon: FileText },
    { to: "/admin/matching", label: "Matching & Shortlist", icon: GitBranch },
    { to: "/admin/agency-pipeline", label: "Agency Pipeline", icon: Award },
    { to: "/admin/projects", label: "Progetti", icon: Briefcase },
    { to: "/admin/meetings", label: "Call", icon: CalendarClock },
    { to: "/admin/proposals", label: "Proposte", icon: FileEdit },
    { to: "/admin/invoices", label: "Fatture", icon: Receipt },
    { to: "/admin/packages", label: "Pacchetti", icon: PackageIcon },
    { to: "/admin/messages", label: "Messaggi", icon: MessageSquare },
    { to: "/admin/referrals", label: "Referral", icon: UserPlus },
    { to: "/admin/faq", label: "FAQ", icon: ListChecks },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/admin/ab-tests", label: "A/B Test", icon: Beaker },
    { to: "/admin/audit-log", label: "Audit log", icon: ScrollText },
    { to: "/admin/channels", label: "Canali & Policy", icon: Sliders },
    { to: "/admin/legal", label: "Legale & GDPR", icon: Shield },
  ],
};

const SidebarNav = ({ items, currentPath, onItemClick }) => (
  <nav className="flex flex-col gap-0.5 px-3">
    {items.map((item) => {
      const Icon = item.icon;
      const active = currentPath === item.to || (item.to !== "/app" && item.to !== "/manager" && item.to !== "/admin" && currentPath.startsWith(item.to));
      return (
        <Link
          key={item.to}
          to={item.to}
          onClick={onItemClick}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-[#EAF0FF] text-[#2D6BFF]" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
          data-testid={`sidebar-link-${item.to.split('/').pop() || 'home'}`}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </Link>
      );
    })}
  </nav>
);

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const items = NAV_BY_ROLE[user?.role] || [];

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await api.get("/notifications/unread-count");
        setUnread(res.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F6F8FC]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[280px] flex-col bg-white border-r border-slate-200" data-testid="app-sidebar">
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <Logo size="md" />
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400 px-6">
            {user?.role === "admin" ? "Admin" : user?.role === "manager" ? "Manager" : "Azienda"}
          </div>
          <SidebarNav items={items} currentPath={location.pathname} />
        </div>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#071225] text-white flex items-center justify-center text-sm font-semibold">
              {user?.full_name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 lg:ml-[280px]" data-testid="app-topbar">
        <div className="h-16 px-4 sm:px-6 flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="app-mobile-menu-button">
                <MenuIcon />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[88%] sm:w-[320px]">
              <div className="h-16 flex items-center px-6 border-b border-slate-200">
                <Logo size="md" />
              </div>
              <div className="py-4">
                <SidebarNav items={items} currentPath={location.pathname} onItemClick={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <div className="lg:hidden">
            <Logo size="sm" />
          </div>

          <div className="flex-1" />

          <LanguageSwitcher />

          <Link to={user?.role === "admin" ? "/admin" : user?.role === "manager" ? "/manager/notifications" : "/app/notifications"}>
            <Button variant="ghost" size="icon" className="relative" data-testid="topbar-notifications">
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D64545] text-white text-[10px] font-semibold flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
              )}
            </Button>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="topbar-user-menu">
                <div className="w-8 h-8 rounded-full bg-[#071225] text-white flex items-center justify-center text-xs font-semibold">
                  {user?.full_name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <ChevronDown size={14} className="hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.full_name}</span>
                  <span className="text-xs text-slate-500">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/")}>
                <Logo size="sm" linkTo={null} />
                <span className="ml-2">Home pubblica</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-[#D64545]" data-testid="logout-btn">
                <LogOut size={16} className="mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="lg:ml-[280px] p-4 sm:p-6 max-w-[1400px]">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
